import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';

import { Pagination } from '../../entities/pagination';
import { strings } from '../../localization/strings';

function generatePageNumbers(current: number, total: number): (number | 'ellipsis')[] {
  const currentPage = current + 1;

  if (total <= 15) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [];

  if (currentPage <= 10) {
    const endPage = currentPage === 10 ? 11 : 10;

    for (let i = 1; i <= Math.min(endPage, total); i += 1) {
      pages.push(i);
    }

    if (total > Math.max(endPage, 15)) {
      pages.push('ellipsis');
      for (let i = total - 4; i <= total; i += 1) {
        pages.push(i);
      }
    }
  } else if (currentPage > total - 5) {
    for (let i = 1; i <= 5; i += 1) {
      pages.push(i);
    }
    pages.push('ellipsis');
    for (let i = total - 4; i <= total; i += 1) {
      pages.push(i);
    }
  } else {
    for (let i = 1; i <= 5; i += 1) {
      pages.push(i);
    }
    pages.push('ellipsis');

    for (let i = currentPage - 2; i <= currentPage + 2; i += 1) {
      if (i > 5 && i < total - 4) {
        pages.push(i);
      }
    }

    pages.push('ellipsis');
    for (let i = total - 4; i <= total; i += 1) {
      pages.push(i);
    }
  }

  const uniquePages: (number | 'ellipsis')[] = [];
  let lastWasEllipsis = false;

  for (const page of pages) {
    if (page === 'ellipsis') {
      if (!lastWasEllipsis) {
        uniquePages.push(page);
        lastWasEllipsis = true;
      }
    } else {
      uniquePages.push(page);
      lastWasEllipsis = false;
    }
  }

  return uniquePages;
}

export default function PaginationComponent({
  pagination,
  onChangePage,
  hasBg,
}: {
  pagination: Pagination;
  onChangePage: (pageNo: number) => void;
  hasBg?: boolean | undefined;
}) {
  return (
    <div className={`flex items-center justify-between py-3  ${hasBg && 'bg-white px-4 sm:px-6'}`}>
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          type="button"
          disabled={pagination.current <= 0}
          onClick={() => onChangePage(pagination.current - 1)}
          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          {strings.common.pagination.previous}
        </button>
        <button
          type="button"
          disabled={pagination.current >= pagination.pages - 1}
          onClick={() => onChangePage(pagination.current + 1)}
          className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          {strings.common.pagination.next}
        </button>
      </div>
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            {strings.common.pagination.showing} {strings.common.pagination.from}{' '}
            <span className="font-medium">{pagination.current * pagination.size + 1}</span>{' '}
            {strings.common.pagination.to}{' '}
            <span className="font-medium">
              {Math.min(pagination.current * pagination.size + pagination.size, pagination.total)}
            </span>{' '}
            {strings.common.pagination.of} <span className="font-medium">{pagination.total}</span>{' '}
            {strings.common.pagination.items}
          </p>
        </div>
        <div>
          <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
            <button
              type="button"
              disabled={pagination.current <= 0}
              onClick={() => onChangePage(pagination.current - 1)}
              className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset bg-white ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
            >
              <span className="sr-only">{strings.common.pagination.previous}</span>
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            {generatePageNumbers(pagination.current, pagination.pages).map((pageItem, index) =>
              pageItem === 'ellipsis' ? (
                <span
                  // eslint-disable-next-line react/no-array-index-key
                  key={`ellipsis-${pagination.current}-${index}`}
                  className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700"
                >
                  ...
                </span>
              ) : (
                <button
                  type="button"
                  aria-current="page"
                  onClick={() => onChangePage(pageItem - 1)}
                  key={`page-${pageItem}`}
                  className={classNames(
                    'relative inline-flex items-center px-4 py-2 text-sm font-semibold focus:z-20',
                    pageItem - 1 === pagination.current
                      ? 'z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                      : 'text-gray-900 ring-1 ring-inset ring-gray-300 bg-white hover:bg-gray-50 focus:outline-offset-0',
                  )}
                >
                  {pageItem}
                </button>
              ),
            )}
            <button
              type="button"
              disabled={pagination.current >= pagination.pages - 1}
              onClick={() => onChangePage(pagination.current + 1)}
              className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 bg-white ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
            >
              <span className="sr-only">{strings.common.pagination.next}</span>
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}
PaginationComponent.defaultProps = {
  hasBg: true,
};
